import React, { useState, useEffect } from 'react'
import { SWRConfiguration } from 'swr'
import useSWRInfinite from 'swr/infinite'
import { SwiperSlide } from 'swiper/react'
import { storyApi } from '@src/api/story'
import { Feed, Brand, Config, Story } from '@api/types'
import { Thumbnail, ThumbnailsCarousel } from '@src/components/thumbnails-carousel'
import LoadingContainer from './loading-container'
import InfiniteScroll from './infinite-scroll'
import flatten from 'lodash/flatten'
import classNames from 'classnames'
import { getEpisodeDateSubtitle, isVideoDateLive } from '@src/utils/vod-series'
import Link from 'next/link'
import { VideoTitle } from './ui/video-title'
import Button from './ui/button'

function FeaturedPlaylist({ feed, feedIndex }: { feed: Feed; feedIndex: number }) {
  const [visibleStories, setVisibleStories] = useState(3)
  const feedStories = feed.stories

  function handleMoreEpisodes() {
    setVisibleStories((prev) => prev + 4)
  }

  const moreButtonVisible = visibleStories < feedStories?.length

  return (
    <div
      className={classNames('bg-[#171717] pt-4 mb-10 -mx-4 flex flex-col items-center', {
        'pb-6': moreButtonVisible
      })}
    >
      <h3 className='self-start pl-4 text-base font-bold text-white sm:px-10'>
        All Episodes ({feedStories.length})
      </h3>
      <div className='w-full'>
        {feedStories.map((story, storyIndex) => {
          const scheduledPost = !isVideoDateLive(story.date)
          const livePostCompleted =
            story.type === 'live' && story.post_type_meta.live?.status === 'post'
          const unpublishedVod = story.type === 'barstool_original' && story.is_published === 0
          const storyDate = story.type === 'live' ? story.post_type_meta.live?.datetime : story.date

          if (livePostCompleted || unpublishedVod || storyIndex > visibleStories) return

          return (
            <Link
              href={`/on-demand/${story.id}/${story.slug}`}
              key={story?.id}
              className={classNames(
                'flex gap-4 py-4 px-4 border-b border-[#282828] cursor-default sm:px-10',
                {
                  'border-none': feedIndex === feedStories.length - 1
                }
              )}
            >
              <Thumbnail
                disableClick
                showTitle={false}
                defaultSizing={false}
                imageClassName='!w-[170px] min-w-[170px] h-[95px] min-h-[95px] max-h-[95px]'
                src={story.thumbnail.desktop}
                disabled={scheduledPost}
                locked={scheduledPost}
                className={scheduledPost ? 'opacity-60' : ''}
                feedIndex={feedIndex}
                event={{
                  id: story.id,
                  name: story.title,
                  thumbnail: story.thumbnail.desktop,
                  slug: story.slug,
                  date: storyDate
                }}
              />
              <VideoTitle
                size='sm'
                title={story.title}
                subtitle={getEpisodeDateSubtitle(story.date)}
              />
            </Link>
          )
        })}
      </div>
      {visibleStories < feedStories?.length && (
        <Button
          className='text-white mt-6 font-bold text-sm py-3 border border-white rounded-[4px] !w-[150px] mx-auto'
          onClick={handleMoreEpisodes}
        >
          More Episodes
        </Button>
      )}
    </div>
  )
}

const LIMIT = 6

const useFeeds = (config: Config, { fallbackData, ...swrConfig }: SWRConfiguration) => {
  const [hasMore, setHasMore] = useState(true)
  const cacheKey = JSON.stringify(config?.videos?.feeds ?? {})

  const fetcher = (pageIndex: number) => {
    if (pageIndex === 0 && fallbackData?.length) return fallbackData[0] ?? []
    const start = pageIndex * LIMIT + 1
    const end = (pageIndex + 1) * LIMIT + 1
    const nextFeed = config.videos.feeds.slice(start, end)
    return storyApi.findFeeds(nextFeed)
  }
  const { data: _feeds = [], ...props } = useSWRInfinite(
    (pageIndex: number) => [pageIndex, cacheKey],
    fetcher,
    { fallbackData, ...swrConfig }
  )
  async function handleNext(size: number) {
    if (!_feeds?.length || !hasMore) return
    props.setSize(size)
  }

  useEffect(() => {
    if (_feeds.length && _feeds[_feeds.length - 1].length < LIMIT) {
      setHasMore(false)
    }
  }, [_feeds.length, _feeds])

  const feeds = flatten(_feeds)
  return { feeds, handleNext, hasMore, ...props }
}

const formatPlaylistsByBrand = (brands: any[], _feeds: any[], liveStory?: Story) => {
  const brandsById = brands.reduce(
    (acc, brand) => ({
      ...acc,
      [brand.id]: brand
    }),
    {}
  )
  const feeds: Feed[] = _feeds.map((feed) =>
    Array.isArray(feed)
      ? { type: 'brand', brand: brandsById[feed[0]?.brand_id], stories: feed }
      : { type: 'playlist', playlist: feed, stories: feed.stories }
  )

  if (!liveStory) return feeds
  else
    return feeds.filter((feed, index) => {
      if (index === 0) {
        return feed?.stories?.length !== 1
      }
      return true
    })
}

export function Feeds({
  feeds = [],
  brands = [],
  config,
  heroVisible,
  liveStory
}: {
  feeds: Feed[]
  brands: Brand[]
  config: Config
  heroVisible?: boolean
  liveStory?: Story
}) {
  const { feeds: _feeds, handleNext, hasMore, size } = useFeeds(config, { fallbackData: [feeds] })
  const formattedFeeds = formatPlaylistsByBrand(brands, _feeds, liveStory)
  const loader = () => <LoadingContainer loading={true} />
  const fetchMore = async function getNext() {
    return handleNext(size + 1)
  }

  return (
    <div
      className={classNames(
        {
          'pb-8': heroVisible,
          'py-8 md:pt-20 lg:pt-40': !heroVisible
        },
        'w-full px-4 overflow-x-hidden overflow-y-visible md:px-0 md:w-auto '
      )}
    >
      <InfiniteScroll loader={loader} next={fetchMore} hasMore={hasMore}>
        {!!formattedFeeds?.length &&
          formattedFeeds.map((feed, index) => {
            switch (feed?.type) {
              case 'brand':
                return (
                  <ThumbnailsCarousel
                    key={feed?.brand?.id ?? index}
                    label={feed?.brand?.name ?? 'video-' + index}
                  >
                    {feed.stories.map((story) => (
                      <SwiperSlide key={story.id}>
                        <Thumbnail
                          src={story.thumbnail.desktop}
                          event={{
                            id: story.id,
                            name: story.title,
                            thumbnail: story.thumbnail.desktop,
                            slug: story.slug
                          }}
                        />
                      </SwiperSlide>
                    ))}
                  </ThumbnailsCarousel>
                )
              case 'playlist':
                return (
                  <>
                    <div className={index === 0 ? 'md:hidden' : 'hidden'}>
                      <FeaturedPlaylist feed={feed} feedIndex={index} />
                    </div>
                    <div className={index === 0 ? 'hidden md:block' : 'block'}>
                      <ThumbnailsCarousel key={feed.playlist.id} label={feed.playlist.title}>
                        {feed?.stories?.map((story) => {
                          const scheduledPost = !isVideoDateLive(story.date)
                          const livePostCompleted =
                            story.type === 'live' && story.post_type_meta.live?.status === 'post'
                          const unpublishedVod =
                            story.type === 'barstool_original' && story.is_published === 0
                          const storyDate =
                            story.type === 'live' ? story.post_type_meta.live?.datetime : story.date

                          if (livePostCompleted || unpublishedVod) return

                          return (
                            <SwiperSlide key={story.id}>
                              <Thumbnail
                                src={story.thumbnail.desktop}
                                disabled={scheduledPost}
                                locked={scheduledPost}
                                disableClick={scheduledPost}
                                className={scheduledPost ? 'opacity-60' : ''}
                                feedIndex={index}
                                event={{
                                  id: story.id,
                                  name: story.title,
                                  thumbnail: story.thumbnail.desktop,
                                  slug: story.slug,
                                  date: storyDate
                                }}
                              />
                            </SwiperSlide>
                          )
                        })}
                      </ThumbnailsCarousel>
                    </div>
                  </>
                )
            }
          })}
      </InfiniteScroll>
    </div>
  )
}
